<script setup>
  import { computed, ref, watch } from "vue";
  import { Tab } from "@/base-components/Headless";
  import Button from "@/base-components/Button";
  import { useRoute, useRouter } from "vue-router";
  import { useFetch } from "@/composables/useFetch.js";
  import { uri } from "@/constants/config.js";
  import { useCountryFlag } from "@/composables/useCountryFlag.js";
  import BaseInputSelect from "@/base-components/Form/BaseInputSelect.vue";
  import moment from "moment-jalaali";
  import Vue3Datatable from "@bhplugin/vue3-datatable";
  import BasePagination from "@/base-components/Table/BasePagination.vue";
  import Lucide from "@/base-components/Lucide";
  import { useI18n } from "vue-i18n";
  import ConfirmationDialog from "@/base-components/Dialog/ConfirmationDialog.vue";
  import UpdatePricingDialog from "@/pages/manager/cloud/sell-pricing/UpdatePricingDialog.vue";
  import DetailPricingDialog from "@/pages/manager/cloud/sell-pricing/DetailPricingDialog.vue";
  import Input from "@/base-components/Form/FormCheck/Input.vue";

  const { t: $t } = useI18n();

  const showDetailPricingDialogFlag = ref(false);
  const regionSelected = ref(null);
  const domainSelected = ref(null);
  const loadingFlag = ref(true);
  const showUpdatePricingDialogFlag = ref(false);
  const showImportDialog = ref(false);
  const pricing = ref({});
  const flags = ref([]);
  const regionsList = ref([]);
  const domainList = ref([]);
  const pricingList = ref([]);
  const loadingPricingFlag = ref(false);
  const showOnlyOverridePricingFlag = ref(false);
  const cols = [
    { field: "counter", title: "#" },
    { field: "id", title: $t("title.id") },
    { field: "name", title: $t("title.name") },
    { field: "type", title: $t("title.type") },
    { field: "network", title: $t("title.networks") },
    { field: "setting", title: $t("title.setting") },
  ];
  const pageOptions = ref({
    count: 1,
    limit: 10,
    page: 1,
  });

  async function preloading() {
    loadingFlag.value = true;
    await Promise.all([fetchRegions(), fetchDomains()]);
    loadingFlag.value = false;
  }

  async function fetchDomains() {
    const { data, error } = await useFetch(uri.admin.cloud.list, {
      method: "GET",
    });
    if (error) return false;
    domainList.value = data.filter((x) => x.chamberType === "Domain");
  }

  async function fetchRegions() {
    const { data, error } = await useFetch(`${uri.api.iaas.region.list}`, {
      method: "GET",
    });
    if (error) return false;
    regionsList.value = data;
    for (const item of data) {
      const index = data.indexOf(item);
      const { flag, getFlag } = useCountryFlag();
      getFlag(item.country);
      flags.value[index] = flag.value;
      regionsList.value[index]["flag"] = flag.value;
      // regionsList.value[index]['aggregate'] = await getAggregateList(item.id);
    }
  }

  async function fetchPricing() {
    if (!domainSelected || !regionSelected) return;
    loadingPricingFlag.value = true;
    const { data, error } = await useFetch(
      uri.admin.cloud.cloudPricing(regionSelected.value.id),
      {
        params: { page: pageOptions.value.page },
        method: "GET",
      }
    );
    if (!error) {
      pricingList.value = data.data.map((x) => ({
        ...x,
        Overrides: x.Overrides.filter(
          (y) => y.chamberId === domainSelected.value.id && y.isBasePrice
        ),
      }));
      pageOptions.value.count = data.meta.total;
      pageOptions.value.limit = data.meta.perPage;
    }
    loadingPricingFlag.value = false;
  }

  function importDialogOnCancel() {
    showImportDialog.value = false;
  }

  const increase = ref(10);
  async function importDialogOnConfirm() {
    showImportDialog.value = false;
    loadingPricingFlag.value = true;
    const body = {
      toChamber: domainSelected.value.id,
      increase: +increase.value,
    };
    await useFetch(
      uri.admin.cloud.cloudPricing(regionSelected.value.id) + "/import",
      { method: "POST", body }
    );
    fetchPricing();
  }

  function showUpdatePricingDialog(value) {
    Object.assign(pricing.value, value);
    showUpdatePricingDialogFlag.value = true;
  }

  function resetData() {
    showUpdatePricingDialogFlag.value = false;
    fetchPricing();
  }

  function showDetailPricing() {
    showDetailPricingDialogFlag.value = true;
  }

  preloading();
  watch(regionSelected, (value) => {
    if (value) {
      fetchPricing();
    }
  });
  watch(domainSelected, (value) => {
    if (value) {
      fetchPricing();
    }
  });
  watch(showOnlyOverridePricingFlag, (value) => {
    fetchPricing();
  });
</script>

<template>
  <div class="grid grid-cols-12 gap-1">
    <div v-if="showImportDialog">
      <ConfirmationDialog
        :close="importDialogOnCancel"
        :show="showImportDialog"
        :accept="importDialogOnConfirm"
        :deny="importDialogOnCancel"
      >
        {{ $t("message.import-increase-by-percent") }}
        <input v-model="increase" />
      </ConfirmationDialog>
    </div>

    <div v-if="showUpdatePricingDialogFlag">
      <UpdatePricingDialog
        v-if="showUpdatePricingDialogFlag"
        @close="resetData"
        @onSuccess="resetData"
        :show="showUpdatePricingDialogFlag"
        :pricing="pricing"
      />
    </div>

    <div v-if="showDetailPricingDialogFlag">
      <DetailPricingDialog
        v-if="showDetailPricingDialogFlag"
        @close="showDetailPricingDialogFlag = false"
        :show="showDetailPricingDialogFlag"
        :pricing-list="pricingList"
        :region="regionSelected"
      />
    </div>

    <div
      class="col-span-12 grid grid-cols-12 gap-3 box mt-2 px-1"
      v-if="!loadingFlag"
    >
      <div class="col-span-2">
        <BaseInputSelect
          class="primary mb-1.5"
          v-model="regionSelected"
          name="id"
          deselect-label=""
          :is-multiple="false"
          track-by="id"
          label="displayName"
          :options="regionsList"
          :searchable="false"
          :allow-empty="false"
          :is-preselect-first="true"
        >
        </BaseInputSelect>
      </div>
      <div class="col-span-1">
        <img
          v-if="regionSelected"
          :src="regionSelected?.flag"
          :alt="regionSelected?.displayName"
          class="w-10 h-10"
        />
      </div>

      <div class="col-span-3">
        <BaseInputSelect
          class="primary mb-1.5"
          v-model="domainSelected"
          name="id"
          deselect-label=""
          :is-multiple="false"
          track-by="id"
          label="displayName"
          :options="domainList"
          :searchable="false"
          :allow-empty="false"
          :is-preselect-first="true"
        >
        </BaseInputSelect>
      </div>
    </div>
    <div class="col-span-12 lg:col-span-12 2xl:col-span-12">
      <!-- BEGIN: Inbox Content -->
      <div class="intro-y box" v-if="regionSelected && domainSelected">
        <div
          class="flex flex-row px-5 pb-5 border-b sm:flex-row text-slate-500 border-slate-200/60 text-end"
        >
          <div class="flex flex-row w-full gap-2 mt-5">
            <input
              type="checkbox"
              class="primary rounded-md mt-0.5"
              v-model="showOnlyOverridePricingFlag"
            />
            <strong>
              {{ $t("message.show-only-need-override-pricing") }}
            </strong>
          </div>
          <div class="flex w-full justify-end items-end">
            <div
              :title="$t('label.import')"
              class="w-5 h-5 ms-5 cursor-pointer"
              @click="showImportDialog = true"
            >
              <Lucide icon="Import" class="w-6 h-6" />
            </div>
            <div class="w-5 h-5 ms-5 cursor-pointer" @click="showDetailPricing">
              <Lucide icon="Eye" class="w-6 h-6" />
            </div>
            <div class="w-5 h-5 ms-5 cursor-pointer" @click="fetchPricing">
              <Lucide icon="RefreshCw" class="w-6 h-6" />
            </div>
          </div>
        </div>

        <div class="mb-5 relative"></div>
        <vue3-datatable
          :columns="cols"
          :rows="pricingList"
          :loading="loadingPricingFlag"
          :totalRows="pageOptions.count"
          :isServerMode="true"
          :pageSize="pageOptions.limit"
          :pagination="false"
          :sortable="false"
          class="advanced-table px-4 text-start items-start justify-start"
          rowClass="cursor-pointer"
        >
          <!-- Empty header slot to remove the header -->
          <template #header>
            <!-- No content here will hide the header -->
          </template>
          <!-- Empty footer slot to remove the footer -->
          <template #footer>
            <!-- No content here will hide the footer -->
          </template>
          <template #counter="data, key">
            <div class="text-start">
              {{
                pricingList.indexOf(data.value) +
                1 +
                (pageOptions.page - 1) * pageOptions.limit
              }}
            </div>
          </template>
          <template #id="data, key">
            <div class="text-start">
              {{ data.value.id }}
            </div>
          </template>
          <template #type="data, key">
            <div class="text-start">
              {{ data.value.assetType }}
            </div>
          </template>
          <template #name="data, key">
            <div class="text-start">
              {{ data.value.assetName }}
            </div>
          </template>
          <template #network="data, key">
            <div class="text-start">
              {{ data.value.Network?.displayName ?? "-" }}
            </div>
          </template>
          <template #setting="data, key">
            <div class="text-start flex gap-3">
              <div
                v-if="data.value.Overrides?.length > 0"
                @click="showUpdatePricingDialog(data.value)"
              >
                <Lucide icon="Edit" class="w-6 h-6 text-gray-700" />
              </div>
              <div :title="$t('label.needImport')" v-else>
                <Lucide
                  icon="Import"
                  class="w-6 h-6 text-red-700"
                  @click="showImportDialog = true"
                />
              </div>
            </div>
          </template>
        </vue3-datatable>
        <div
          class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-left text-slate-500"
        >
          <div>
            <BasePagination
              v-if="pageOptions.count > pageOptions.limit"
              :page-options="pageOptions"
              @page-change="fetchPricing"
            />
          </div>
          <div class="mt-2 sm:ml-auto sm:mt-0"></div>
        </div>
      </div>
      <!-- END: Inbox Content -->
    </div>
  </div>
</template>

<style scoped>
  button {
    transition: all 0.3s ease;
  }

  button:hover {
    transform: scale(1.05);
  }
</style>
